import { useFlag } from '@unleash/proxy-client-react';
import session from 'lib/session';
import { actions } from 'modules/user';
import semver from 'semver';

export const accountMenu = {
  title: 'account',
  icon: 'person',
  link: '/account',
  onClick: () => {}, // Prevents redirect to blank non existing route
  items: [
    {
      title: 'profile',
      link: '/account/profile',
    },
    {
      title: 'users',
      link: '/account/users',
    },
    {
      title: 'billing',
      link: '/account/billing',
      hideIf: 'testStore',
      condition: (merchant, user) =>
        !merchant.merchant_regions?.includes('AU') || user.isAdmin,
    },
    {
      title: 'Shopify plan',
      link: '/account/shopify-plan',
      condition: (merchant) =>
        merchant?.platform === 'shopify' && !!merchant?.shopifyLiquid,
    },
    {
      title: 'log_out',
      link: '/account/log-out',
      onClick: ({ dispatch }) => {
        if (session.kcToken) {
          return dispatch(actions.kcSignOut());
        }
        return dispatch(actions.signOut());
      },
    },
  ],
};

export const adminMenu = {
  title: 'admin_area',
  icon: 'person',
  link: '/admin',
  linkTo: '/admin/config',
  items: [
    {
      title: 'mjs_configuration',
      link: '/admin/config',
    },
    {
      title: 'personalization',
      link: '/admin/personalization',
    },
    {
      title: 'search_configuration',
      link: '/admin/search-configuration',
    },
    {
      title: 'mjs_script_injections',
      link: '/admin/injections',
    },
    {
      title: 'react_components',
      link: '/admin/components',
    },
    {
      title: 'custom_css',
      link: '/admin/css',
    },
    {
      title: 'upgrade_plan',
      link: '/admin/update-plan',
    },
    {
      title: 'generate_shopify_link',
      link: '/admin/generate-shopify-link',
      condition: (merchant) => merchant.platform === 'shopify',
    },
    {
      title: 'Cancel Shopify Charge',
      link: '/admin/cancel-shopify-charge',
      condition: (merchant) => merchant.platform === 'shopify',
    },
    {
      title: 'manage_shopify_metafields',
      link: '/admin/manage-shopify-metafields',
      condition: (merchant) => merchant.platform === 'shopify',
    },
    {
      title: 'update_platform',
      link: '/admin/merchant-info',
    },
    {
      title: 'api_configuration',
      link: '/admin/api-config',
    },
    {
      title: 'update_merchant_tier',
      link: '/admin/update-merchant-tier',
    },
    {
      title: 'manage_merchant_tiers',
      link: '/admin/merchant-tiers',
    },
  ],
};

export const productSync = {
  title: 'Product sync',
  link: '/setup/product-sync/overview',
  icon: 'plug',
};

export default [
  {
    title: 'search',
    icon: 'search',
    link: '/solutions/search',
    items: [
      {
        title: 'pinning',
        link: '/solutions/search/pinning',
        feature: 'pin',
      },
      {
        title: 'merchandising_rules',
        link: '/solutions/search/rules',
        feature: 'useLucy',
      },
      {
        title: 'promo_cards',
        link: '/solutions/search/promos',
        condition: (merchant) => merchant.mjsVersion.startsWith('7'),
      },
      {
        title: 'banners',
        link: '/solutions/search/banners',
        feature: 'banner',
        condition: ({ shopifyLiquid }) => !shopifyLiquid,
      },
      {
        title: 'blacklisting',
        link: '/solutions/search/blacklisting',
        items: [
          {
            title: 'search_suggestions',
            link: '/solutions/search/blacklisting/suggestions',
          },
          {
            title: 'product_matches',
            link: '/solutions/search/blacklisting/products',
          },
        ],
      },
      {
        title: 'Synonyms',
        link: '/solutions/search/synonyms',
        feature: 'synonym',
      },
      // {
      //   title: 'two_way_synonyms',
      //   link: '/solutions/search/synonyms/manual',
      //   feature: 'synonym',
      // },
      {
        title: 'page_redirects',
        link: '/solutions/search/redirects',
        feature: 'redirect',
      },
      {
        title: 'search_preview',
        link: '/solutions/search/preview',
        isNew: true,
        condition: () => useFlag('use_search_preview'),
      },
      {
        title: 'content',
        link: '/solutions/search/content',
        isNew: true,
        dark: true,
        condition: (merchant) =>
          semver.gt(merchant.mjsVersion, '7.1.0') &&
          merchant.features.features.content &&
          merchant.features.features.content.enabled,
      },
    ],
  },
  {
    title: 'smart_collections',
    icon: 'view-compact',
    link: '/solutions/smart-collections',
    feature: 'smart-collection',
    items: [
      {
        title: 'collections',
        link: '/solutions/smart-collections/collections',
      },
      {
        title: 'pinning',
        link: '/solutions/smart-collections/pinning',
        feature: 'pin',
      },
      {
        title: 'merchandising_rules',
        link: '/solutions/smart-collections/rules',
        feature: 'useLucy',
      },
      {
        title: 'promo_cards',
        link: '/solutions/smart-collections/promos',
        condition: (merchant) => merchant.mjsVersion.startsWith('7'),
      },
      {
        title: 'banners',
        link: '/solutions/smart-collections/banners',
        feature: 'banner',
        condition: ({ shopifyLiquid }) => !shopifyLiquid,
      },
    ],
  },
  {
    title: 'recommendations',
    icon: 'view-carousel',
    link: '/solutions/recommendations',
    dark: true,
  },
  {
    title: 'analytics',
    link: '/analytics',
    icon: 'pie-chart',
    items: [
      {
        title: 'snapshot',
        link: '/analytics/snapshot',
        items: [
          {
            title: 'revenue',
            link: '/analytics/snapshot#revenue',
          },
          {
            title: 'out_of_total',
            link: '/analytics/snapshot#outOfTotal',
          },
          {
            title: 'avg_order_value',
            link: '/analytics/snapshot#aov',
          },
          {
            title: 'products_sold',
            link: '/analytics/snapshot#productsSold',
          },
          {
            title: 'visits',
            link: '/analytics/snapshot#visits',
          },
          {
            title: 'unique_visitors',
            link: '/analytics/snapshot#visitors',
          },
        ],
      },
      {
        title: 'search',
        link: '/analytics/search',
        items: [
          {
            title: 'top_searches_by_volume',
            link: '/analytics/search#byVolume',
          },
          {
            title: 'top_0_results',
            link: '/analytics/search#top0Results',
          },
          {
            title: 'top_partial_matches',
            link: '/analytics/search#topPartialMatches',
          },
          {
            title: 'top_filters',
            link: '/analytics/search#topFilters',
          },
        ],
      },
      {
        title: 'smart_collections',
        link: '/analytics/collections',
        items: [
          {
            title: 'top_smart_collections',
            link: '/analytics/collections#bySlot',
          },
          {
            title: 'top_used_filters',
            link: '/analytics/collections#filters',
          },
        ],
      },
      {
        title: 'recommendations',
        link: '/analytics/recommendations',
        items: [
          {
            title: 'recommendation_performance',
            link: '/analytics/recommendations#performance',
          },
        ],
      },
    ],
  },
  {
    title: 'settings',
    icon: 'plug',
    link: '/setup',
    items: [
      {
        title: 'status_and_usage',
        link: '/setup/integration',
      },
      {
        title: 'product_sync',
        link: '/setup/product-sync',
        items: [
          {
            title: 'overview',
            link: '/setup/product-sync/overview',
          },
        ],
      },
      {
        title: 'integrations',
        link: '/setup/integrations',
      },
      {
        title: 'primary_setup',
        link: '/setup/primary',
        linkTo: '/setup/primary/filters',
        items: [
          {
            title: 'filtering',
            link: '/setup/primary/filters',
          },
          // {
          //   title: 'Stickers',
          //   link: '/setup/primary/stickers'
          // },
          {
            title: 'color_mapping',
            link: '/setup/primary/color-mapping',
          },
          {
            title: 'stock',
            link: '/setup/primary/stock',
          },
          {
            title: 'translations',
            link: '/setup/primary/translations',
          },
        ],
      },
      {
        title: 'advanced_setup',
        link: '/setup/advanced/',
        linkTo: '/setup/advanced/ip-blocking',
        items: [
          {
            title: 'ip_blocking',
            link: '/setup/advanced/ip-blocking',
          },
          {
            title: 'general',
            link: '/setup/advanced/general',
            condition: (merchant) => merchant.mjsVersion.startsWith('7'),
          },
          {
            title: 'search_collections',
            link: '/setup/advanced/search',
            condition: (merchant) => merchant.mjsVersion.startsWith('7'),
          },
          {
            title: 'autocomplete',
            link: '/setup/advanced/autocomplete',
            condition: (merchant) =>
              merchant.mjsVersion.startsWith('7') && !merchant.shopifyLiquid,
          },
          {
            title: 'recommendations',
            link: '/setup/advanced/recommendations',
            condition: ({ mjsVersion, shopifyLiquid }) =>
              mjsVersion.startsWith('7') && !shopifyLiquid,
          },
          {
            title: 'frontend_configuration',
            link: '/setup/advanced/frontend',
            condition: (merchant) => !merchant.mjsVersion.startsWith('7'),
            items: [
              {
                title: 'rendering',
                link: '/setup/advanced/frontend#rendering',
              },
              {
                title: 'currency_and_price',
                link: '/setup/advanced/frontend#currency',
              },
              {
                title: 'product_display',
                link: '/setup/advanced/frontend#product',
              },
              {
                title: 'autocomplete',
                link: '/setup/advanced/frontend#autocomplete',
              },
              {
                title: 'search_results',
                link: '/setup/advanced/frontend#results',
              },
              {
                title: 'filtering',
                link: '/setup/advanced/frontend#filtering',
              },
              {
                title: 'pagination',
                link: '/setup/advanced/frontend#pagination',
              },
              {
                title: 'sorting',
                link: '/setup/advanced/frontend#sorting',
              },
            ],
          },
          {
            title: 'platform_configuration',
            link: '/setup/advanced/platform-configuration',
            condition: (_, user) => user.isAdmin,
          },
          {
            title: 'trend_scoring',
            link: '/setup/advanced/trend-score',
            isNew: true,
          },
        ],
      },
    ],
  },
  {
    title: 'Multimarket settings',
    icon: 'globe',
    link: '/multimarket-settings',
    onClick: () => {},
    condition: () => useFlag('multimarket'),
    items: [
      {
        title: 'Markets',
        link: '/multimarket-settings/markets',
        condition: () => useFlag('multimarket'),
      },
      {
        title: 'Regions',
        link: '/multimarket-settings/regions',
        condition: () => useFlag('multimarket'),
      },
      {
        title: 'Languages',
        link: '/multimarket-settings/languages',
        condition: () => useFlag('multimarket'),
      },
      {
        title: 'Warehouses',
        link: '/multimarket-settings/warehouses',
        condition: () => useFlag('multimarket'),
      },
      {
        title: 'Currencies',
        link: '/multimarket-settings/currencies',
        condition: () => useFlag('multimarket'),
      },
    ],
  },
];
